import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PrimeNG } from 'primeng/config';
import { TranslocoService } from '@jsverse/transloco';
import { type Locale } from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns';
import { switchMap } from 'rxjs/operators';
import type { AvailableLanguages } from '@camino-solutions/core/i18n';
import { assertAvailableLanguages } from '@camino-solutions/core/i18n';
import { registerLocaleData } from '@angular/common';

@Component({
  standalone: true,
  imports: [RouterModule],
  selector: 'cs-portal-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  readonly #primeng = inject(PrimeNG);
  readonly #translocoService = inject(TranslocoService);

  constructor() {
    // Subscribe to language changes
    this.#translocoService.langChanges$
      .pipe(
        switchMap(lang => {
          assertAvailableLanguages(lang);
          this.setDateFnsLocale(lang);
          return this.#translocoService.selectTranslateObject('PRIMENG');
        }),
      )
      .subscribe(translation => this.#primeng.setTranslation(translation));

    // // Example usage
    // const scheduler = new TaskScheduler();
    //
    // const initialTasks = [
    //   { taskId: 'task1', delay: 3000 },
    //   { taskId: 'task2', delay: 5000 },
    // ];
    //
    // scheduler.addTasks(initialTasks);
    //
    // // Adding more tasks after some delay
    // setTimeout(() => {
    //   const moreTasks = [
    //     { taskId: 'task3', delay: 2000 },
    //     { taskId: 'task4', delay: 4000 },
    //   ];
    //   scheduler.addTasks(moreTasks);
    // }, 7000); // 7 seconds delay to allow initial tasks to complete
    //
    // // Removing a task after some delay
    // setTimeout(() => {
    //   // scheduler.removeTasks(['task1']);
    // }, 300);
  }

  private setDateFnsLocale(lang: AvailableLanguages) {
    Promise.all([this.getLocale(lang), this.getAngularLocale(lang)]).then(
      ([locale, angularLocaleData]) => {
        if (angularLocaleData) {
          registerLocaleData(angularLocaleData.default, lang);
        }
        setDefaultOptions({ locale });

        console.info('Language changed to:', lang);
      },
    );
  }

  private getAngularLocale(lang: AvailableLanguages): Promise<any> {
    switch (lang) {
      case 'hu':
        return import('@angular/common/locales/hu');
      case 'en':
        return Promise.resolve(null); // English is the default locale
    }
  }

  private getLocale(lang: AvailableLanguages): Promise<Locale> {
    switch (lang) {
      case 'hu':
        return import('date-fns/locale/hu').then(m => m.hu);
      case 'en':
        return import('date-fns/locale/en-US').then(m => m.enUS);
    }
  }
}
